import { Space, Tag, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { shortId } from '@/shared/lib/shortId'
import { isEquipmentOwnerType, useOwnerLabel } from './ownerApi'

interface EquipmentOwnerRefProps {
  ownerType: string | null
  ownerId: string | null
  /** Kod/login ham ko'rsatilsinmi — kartochkada ha, tor ustunda yo'q */
  showHint?: boolean
}

/**
 * Jihozning egasi — xom UUID o'rniga NOM.
 *
 * <h2>Nega kerak (XSB-003)</h2>
 * Ilgari bu yerda `Tag` ichida tur kodi va qisqartirilgan id turardi
 * (`SUBSCRIBER` + `2e97386c…`). Operator qaysi abonentda ekanini BILA
 * OLMASDI — ya'ni ustun bor edi, ma'lumot esa yo'q. Eski izohda sabab
 * "panel id ni nomga o'girish uchun kerakli so'rovni bilmaydi" deb
 * yozilgan edi; endi biladi (`ownerApi.fetchOwner`).
 *
 * <h2>⚠️ Besh holat ALOHIDA — `AgentRef` va `SiteRef` bilan bir xil qoida</h2>
 *   · egasiz               → chiziqcha (bu NORMAL, xato emas)
 *   · `OTHER` turi         → tur nomi + id (ro'yxati yo'q, `ownerApi` izohi)
 *   · huquq yo'q           → tur nomi + qisqartirilgan id, sababi tooltipda.
 *                            "Noma'lum" deb AYBLANMAYDI: ega bor, bizga
 *                            ko'rinmaydi
 *   · topilmadi (404)      → sariq ogohlantirish; havola OSILIB QOLGAN
 *   · topildi              → nom (+ login/kod)
 *
 * Bo'sh katak bu beshtasini bir xil ko'rsatardi va aynan shu chalkashlik
 * XSB-003 hisobotining boshlanishi edi.
 */
export function EquipmentOwnerRef({ ownerType, ownerId, showHint = true }: EquipmentOwnerRefProps) {
  const { t } = useTranslation()
  const label = useOwnerLabel(ownerType, ownerId)

  if (ownerType === null && ownerId === null) {
    return <Typography.Text type="secondary">{t('inventory.noOwner')}</Typography.Text>
  }

  const typeName = isEquipmentOwnerType(ownerType)
    ? t(`equipmentOwnerType.${ownerType}`)
    : (ownerType ?? t('common.dash'))
  // ⚠️ B12 — qisqartirish `shared/lib/shortId.ts` da. O'zgaruvchi nomi
  //    ATAYLAB `ownerShortId`: ilgari u `shortId` deb atalardi va
  //    funksiyani SOYALAB qo'yardi — ya'ni umumiy shaklga o'tish
  //    jimgina bekor bo'lardi.
  const ownerShortId = ownerId === null ? t('common.dash') : shortId(ownerId)

  if (!label.resolvable) {
    return (
      <Space size={4} wrap>
        <Tag>{typeName}</Tag>
        <Typography.Text type="secondary" code style={{ fontSize: 12 }}>
          {ownerShortId}
        </Typography.Text>
      </Space>
    )
  }

  if (!label.allowed) {
    return (
      <Tooltip title={t('inventory.ownerNoPermissionRef')}>
        <Space size={4} wrap>
          <Tag>{typeName}</Tag>
          <Typography.Text type="secondary" code style={{ fontSize: 12 }}>
            {ownerShortId}
          </Typography.Text>
        </Space>
      </Tooltip>
    )
  }

  if (label.loading) {
    return <Typography.Text type="secondary">{t('common.loading')}</Typography.Text>
  }

  if (label.missing) {
    return (
      <Tooltip title={t('inventory.ownerMissingHint')}>
        <Space size={4} wrap>
          <Tag>{typeName}</Tag>
          <Tag color="warning">{t('inventory.ownerMissing')}</Tag>
        </Space>
      </Tooltip>
    )
  }

  return (
    <Space size={4} wrap>
      <Tag>{typeName}</Tag>
      <Typography.Text>{label.name}</Typography.Text>
      {showHint && label.hint ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {label.hint}
        </Typography.Text>
      ) : null}
    </Space>
  )
}
